import { MessageGroup } from "@somethings-ui/web";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  isUnsupportedAudio,
  loadPlayableAudio,
} from "@/lib/audio/repair-unplayable-audio";
import { useResolveFileMessage } from "@/lib/api/messages";
import { useAudioTranscoder } from "@/lib/hooks/use-audio-transcoder";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";

export function AttentionVoiceNote({
  src,
  sdkId,
  reqId,
}: {
  src: string | undefined;
  sdkId: string | null;
  reqId: string | null;
}) {
  const { track } = useTrackEvent();
  const { transcodeToM4a } = useAudioTranscoder();
  const [playableSrc, setPlayableSrc] = useState(src);
  const [repairing, setRepairing] = useState(false);
  const { transcription, isFetching, requestTranscript } =
    useResolveFileMessage(sdkId, reqId, { eager: false });

  useEffect(() => {
    if (!src) {
      setPlayableSrc(undefined);
      setRepairing(false);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setPlayableSrc(src);
    setRepairing(false);
    const audio = new Audio();
    audio.preload = "metadata";
    const onError = () => {
      const code = audio.error?.code ?? null;
      if (cancelled || !isUnsupportedAudio(code)) return;
      setRepairing(true);
      void loadPlayableAudio(src, transcodeToM4a).then((blob) => {
        if (cancelled || !blob) {
          if (!cancelled) setRepairing(false);
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
          return;
        }
        setPlayableSrc(objectUrl);
        setRepairing(false);
      });
    };
    audio.addEventListener("error", onError);
    audio.src = src;
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      audio.removeEventListener("error", onError);
      audio.pause();
      audio.removeAttribute("src");
    };
  }, [src, transcodeToM4a]);

  const handleTranscriptOpen = useCallback(
    (hadTranscript: boolean) => {
      track(PostHogEvents.VOICE_NOTE_TRANSCRIPT_VIEWED, {
        had_transcript: hadTranscript,
      });
    },
    [track],
  );

  if (repairing) {
    return (
      <p className="flex items-center gap-2 text-sm text-zinc-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Preparing audio…
      </p>
    );
  }

  return (
    <MessageGroup.Media.Audio
      src={playableSrc}
      transcription={transcription}
      transcriptionPending={isFetching}
      onRequestTranscript={requestTranscript}
      onTranscriptOpen={handleTranscriptOpen}
    />
  );
}
